import React, { useEffect, useRef, useState } from 'react';
import { Calendar } from 'lucide-react';

// =============================================================================
// DateFilter.jsx — the ONE date module for the whole app.
//
//   1. <DateFilter>      the reusable filter UI (default export)
//   2. <DateInput>       DD-MM-YYYY text input (+ calendar picker) used for every
//                        date the user types or selects, replacing <input type="date">
//   3. Date utilities    parseDateValue / formatDisplayDate / toYMD ...
//   4. Filtering engine  matchesDateFilter / filterByDate (+ presets)
//
// Dashboards never hard-code one global date field. Each passes the field(s) that
// are the primary date for THAT screen:
//
//   <DateFilter dateField="interview_date" includeBlank ... />            Interviews
//   <DateFilter dateField="date_received" ... />                          Job Gathering
//   <DateFilter dateField={['assigned_date','reassigned_date']} ... />    Tasks
//   rows.filter(r => matchesDateFilter(r, state, 'interview_date'))       engine
//
// `dateField` config (for the engine, and for the component prop):
//   'field'                           one field on the row
//   ['a','b']                         row matches if ANY of the fields matches
//   (row) => value | value[]          custom accessor
//   { field | fields | get, includeBlank }   includeBlank: rows with NO date still
//                                     match every mode (used by Interview Date)
//
// Internal state is always ISO ('YYYY-MM-DD'); everything the user SEES or TYPES
// is DD-MM-YYYY. Dates are handled as local calendar days — never via
// toISOString() — so there is no off-by-one-day shift (e.g. in IST).
// =============================================================================

const pad = (n) => String(n).padStart(2, '0');

// ---- Date utilities -----------------------------------------------------------

// Local calendar day as 'YYYY-MM-DD'. Deliberately NOT toISOString() (that
// converts to UTC first and shifts the day in timezones ahead of UTC).
export const toYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const isRealDate = (y, m, d) => {
    if (m < 1 || m > 12 || d < 1) return false;
    return d <= new Date(y, m, 0).getDate();
};

// Any supported input -> 'YYYY-MM-DD' (local calendar day) or null.
// Accepts: Date, 'YYYY-MM-DD', 'DD-MM-YYYY', and ISO timestamps. A timestamp that
// carries a timezone (Z / +05:30) is converted to the viewer's local day; a
// zone-less one keeps its own date. null/''/garbage/impossible dates -> null.
export function parseDateValue(value) {
    if (value === null || value === undefined || value === '') return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : toYMD(value);
    const str = String(value).trim();
    if (!str) return null;

    let m = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return isRealDate(+m[1], +m[2], +m[3]) ? str : null;

    m = str.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (m) return isRealDate(+m[3], +m[2], +m[1]) ? `${m[3]}-${m[2]}-${m[1]}` : null;

    m = str.match(/^(\d{4})-(\d{2})-(\d{2})[T ]/);
    if (m) {
        if (!isRealDate(+m[1], +m[2], +m[3])) return null;
        if (/(Z|[+-]\d{2}:?\d{2})$/.test(str)) {
            const d = new Date(str.replace(' ', 'T'));
            return isNaN(d.getTime()) ? null : toYMD(d);
        }
        return `${m[1]}-${m[2]}-${m[3]}`;
    }
    return null;
}

// Display only (DD-MM-YYYY). Empty -> '-'. Unparseable text is returned as-is.
// Filtering/sorting must keep using the ISO value, never this string.
export function formatDisplayDate(value) {
    if (value === null || value === undefined || value === '') return '-';
    const ymd = parseDateValue(value);
    if (!ymd) return String(value);
    const [y, mo, d] = ymd.split('-');
    return `${d}-${mo}-${y}`;
}
// Same behaviour for full timestamps (shown as the viewer's local day).
export const formatTimestampDate = formatDisplayDate;

const addDays = (ymd, n) => {
    const [y, m, d] = ymd.split('-').map(Number);
    return toYMD(new Date(y, m - 1, d + n));
};
const weekRangeOf = (ymd) => {           // Monday..Sunday containing ymd
    const [y, m, d] = ymd.split('-').map(Number);
    const dow = new Date(y, m - 1, d).getDay();   // 0 = Sunday
    const start = addDays(ymd, dow === 0 ? -6 : 1 - dow);
    return [start, addDays(start, 6)];
};
const isoWeekRange = (year, week) => {   // legacy 'YYYY-Www' values
    const [mon1] = weekRangeOf(`${year}-01-04`);
    const start = addDays(mon1, (week - 1) * 7);
    return [start, addDays(start, 6)];
};
const monthRangeOf = (year, month) => [
    `${year}-${pad(month)}-01`,
    `${year}-${pad(month)}-${pad(new Date(year, month, 0).getDate())}`
];

// ---- Filter state + presets -------------------------------------------------

export const DATE_FILTER_MODES = [
    { mode: 'All', label: 'All' },
    // { mode: 'Today', label: 'Today' },
    // { mode: 'Yesterday', label: 'Yesterday' },
    // { mode: 'This Week', label: 'This Week' },
    { mode: 'Day-wise', label: 'Day-wise' },
    // { mode: 'Weekly', label: 'Weekly' },
    // { mode: 'Monthly', label: 'Monthly' },
    { mode: 'This Month', label: 'This Month' },
    { mode: 'Range', label: 'Custom Date' }
];

export const todayDateFilter = () => ({ mode: 'Day-wise', dateValue: toYMD(new Date()) });
export const allDateFilter = () => ({ mode: 'All' });

// True when a filter is still "Day-wise = today" (or the live Today preset).
export const isTodayFilter = (f) =>
    !!f && ((f.mode === 'Day-wise' && f.dateValue === toYMD(new Date())) || f.mode === 'Today');
export const isDefaultDateFilter = (f, dateDefault = 'today') =>
    dateDefault === 'all' ? (!f || f.mode === 'All') : isTodayFilter(f);

// The inclusive [start, end] ('YYYY-MM-DD'; either side may be null = open) a
// filter selects, or null when it selects everything (All / incomplete / invalid).
export function resolveDateRange(state, now = new Date()) {
    if (!state || !state.mode || state.mode === 'All') return null;
    const today = toYMD(now);
    switch (state.mode) {
        case 'Today': return [today, today];
        case 'Yesterday': { const y = addDays(today, -1); return [y, y]; }
        case 'This Week': return weekRangeOf(today);
        case 'This Month': return monthRangeOf(now.getFullYear(), now.getMonth() + 1);
        case 'Day-wise': {
            const d = parseDateValue(state.dateValue);
            return d ? [d, d] : null;
        }
        case 'Weekly': {
            const v = String(state.dateValue || '');
            const w = v.match(/^(\d{4})-W(\d{1,2})$/);
            if (w) return isoWeekRange(+w[1], +w[2]);
            const d = parseDateValue(v);
            return d ? weekRangeOf(d) : null;
        }
        case 'Monthly': {
            const m = String(state.dateValue || '').match(/^(\d{4})-(\d{2})$/);
            return m && +m[2] >= 1 && +m[2] <= 12 ? monthRangeOf(+m[1], +m[2]) : null;
        }
        case 'Range': {
            const s = parseDateValue(state.startDate);
            const e = parseDateValue(state.endDate);
            if (!s && !e) return null;
            return [s, e];
        }
        default: return null;
    }
}

// ---- Filtering engine --------------------------------------------------------

const normalizeCfg = (cfg, state) => {
    let c = cfg;
    if (c === undefined || c === null) c = state && state.dateField;   // state carries its own field
    const stateBlank = !!(state && state.includeBlank);
    if (typeof c === 'function') return { get: c, includeBlank: stateBlank };
    if (typeof c === 'string' || Array.isArray(c)) return { fields: [].concat(c), includeBlank: stateBlank };
    if (c && typeof c === 'object') {
        return {
            fields: c.fields || (c.field ? [c.field] : undefined),
            get: c.get,
            includeBlank: c.includeBlank !== undefined ? !!c.includeBlank : stateBlank
        };
    }
    return { includeBlank: stateBlank };
};

// Does `target` satisfy the date filter `state`?
//   target: a raw date value, or a row (then `dateField` says which field(s)).
// Inactive ('All'), incomplete (e.g. Day-wise with no date yet) and invalid
// filters match everything. An active filter does not match a row with no/invalid
// date unless includeBlank is set.
export function matchesDateFilter(target, state, dateField) {
    if (!state || !state.mode || state.mode === 'All') return true;
    const range = resolveDateRange(state);
    if (!range) return true;
    if (range[0] && range[1] && range[0] > range[1]) return false;   // From after To

    const cfg = normalizeCfg(dateField, state);
    let raw;
    if (cfg.get) raw = cfg.get(target);
    else if (cfg.fields && target && typeof target === 'object' && !(target instanceof Date)) raw = cfg.fields.map(f => target[f]);
    else raw = target;

    const days = [].concat(raw === undefined ? [] : raw).map(parseDateValue).filter(Boolean);
    if (days.length === 0) return cfg.includeBlank;
    return days.some(d => (!range[0] || d >= range[0]) && (!range[1] || d <= range[1]));
}

export const filterByDate = (rows, state, dateField) =>
    (rows || []).filter(r => matchesDateFilter(r, state, dateField));

// ---- DD-MM-YYYY text input --------------------------------------------------

const maskDigits = (digits, groups) => {   // groups e.g. [2,2,4] -> 'DD-MM-YYYY'
    let out = '', i = 0;
    groups.forEach((g, gi) => {
        const part = digits.slice(i, i + g);
        i += g;
        if (!part) return;
        out += (gi > 0 ? '-' : '') + part;
    });
    return out;
};

// Shared text-entry engine for date (DD-MM-YYYY) and month (MM-YYYY) values.
function MaskedDateField({ kind, value, onCommit, min, max, disabled, required, readOnly, name, style, placeholder, ariaLabel }) {
    const isMonth = kind === 'month';
    const groups = isMonth ? [2, 4] : [2, 2, 4];
    const show = (v) => {
        if (!v) return '';
        if (isMonth) { const m = String(v).match(/^(\d{4})-(\d{2})$/); return m ? `${m[2]}-${m[1]}` : ''; }
        const ymd = parseDateValue(v);
        return ymd ? formatDisplayDate(ymd) : '';
    };
    const parse = (text) => {
        const digits = text.replace(/\D/g, '');
        if (isMonth) {
            if (digits.length !== 6) return null;
            const mo = +digits.slice(0, 2);
            return mo >= 1 && mo <= 12 ? `${digits.slice(2)}-${digits.slice(0, 2)}` : null;
        }
        if (digits.length !== 8) return null;
        return parseDateValue(`${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`);
    };

    const [text, setText] = useState(show(value));
    const [focused, setFocused] = useState(false);
    const pickerRef = useRef(null);

    useEffect(() => { if (!focused) setText(show(value)); }, [value, focused]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (e) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, isMonth ? 6 : 8);
        const masked = maskDigits(digits, groups);
        setText(masked);
        if (digits.length === 0) { onCommit(''); return; }
        const parsed = parse(masked);
        if (parsed) onCommit(parsed);
    };
    const handleBlur = () => {
        setFocused(false);
        const parsed = parse(text);
        if (text && !parsed) setText(show(value));      // invalid / incomplete -> revert
        else setText(show(parsed || value));
    };
    const openPicker = () => {
        const el = pickerRef.current;
        if (!el || disabled || readOnly) return;
        try { if (el.showPicker) { el.showPicker(); return; } } catch (err) { /* fall through */ }
        el.focus(); el.click();
    };

    return (
        <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', width: style && style.width ? style.width : undefined }}>
            <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                name={name}
                aria-label={ariaLabel || (isMonth ? 'Month (MM-YYYY)' : 'Date (DD-MM-YYYY)')}
                placeholder={placeholder || (isMonth ? 'MM-YYYY' : 'DD-MM-YYYY')}
                value={text}
                maxLength={isMonth ? 7 : 10}
                disabled={disabled}
                readOnly={readOnly}
                required={required}
                onFocus={() => setFocused(true)}
                onChange={handleChange}
                onBlur={handleBlur}
                style={{ padding: '6px 30px 6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none', boxSizing: 'border-box', width: '100%', minWidth: isMonth ? '90px' : '120px', ...style }}
            />
            {!isMonth && !disabled && !readOnly && (
                <>
                    <button
                        type="button"
                        tabIndex={-1}
                        aria-label="Open calendar"
                        onClick={openPicker}
                        style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex' }}
                    >
                        <Calendar size={14} color="#64748b" />
                    </button>
                    <input
                        ref={pickerRef}
                        type="date"
                        tabIndex={-1}
                        aria-hidden="true"
                        value={parseDateValue(value) || ''}
                        min={min || undefined}
                        max={max || undefined}
                        onChange={(e) => { const p = parseDateValue(e.target.value); if (p) { setText(show(p)); onCommit(p); } }}
                        style={{ position: 'absolute', right: 0, bottom: 0, width: 0, height: 0, opacity: 0, pointerEvents: 'none', border: 0, padding: 0 }}
                    />
                </>
            )}
        </span>
    );
}

// Drop-in replacement for <input type="date">: value is ISO 'YYYY-MM-DD', the user
// sees/types DD-MM-YYYY, and onChange still receives an event-like
// { target: { name, value: 'YYYY-MM-DD' | '' } } so existing handlers keep working.
export function DateInput({ value, onChange, name, min, max, disabled, required, readOnly, style, placeholder, 'aria-label': ariaLabel }) {
    return (
        <MaskedDateField
            kind="date"
            value={value}
            min={min}
            max={max}
            name={name}
            disabled={disabled}
            required={required}
            readOnly={readOnly}
            style={style}
            placeholder={placeholder}
            ariaLabel={ariaLabel}
            onCommit={(iso) => onChange && onChange({ target: { name, value: iso } })}
        />
    );
}

// ---- The reusable filter component ------------------------------------------

// Only the four filter fields identify a filter's selection.
const core = (s) => ({
    mode: (s && s.mode) || 'All',
    dateValue: (s && s.dateValue) || '',
    startDate: (s && s.startDate) || '',
    endDate: (s && s.endDate) || ''
});
const sameCore = (a, b) => a.mode === b.mode && a.dateValue === b.dateValue && a.startDate === b.startDate && a.endDate === b.endDate;

const btn = (active) => ({
    background: active ? '#2563eb' : '#f1f5f9',
    color: active ? '#fff' : '#475569',
    border: 'none', padding: '6px 12px', borderRadius: '6px',
    fontSize: '12px', fontWeight: '600', cursor: 'pointer'
});
const hint = { fontSize: '12px', color: '#64748b' };

// Props:
//   initialValue / onFilterChange  state in/out ({mode,dateValue,startDate,endDate}).
//                                  The component re-syncs whenever initialValue
//                                  changes, so a parent can reset it by changing state.
//   dateField     the primary date field(s) of the screen using this filter (see top).
//                 It is attached to every emitted state as `dateField`.
//   includeBlank  rows with no date still match (Interview Date).
//   modes         optional list of mode names to offer (default: all).
export default function DateFilter({ initialValue, onFilterChange, dateField, includeBlank = false, modes, style }) {
    const [state, setState] = useState(() => core(initialValue));

    const iv = initialValue || {};
    useEffect(() => {
        const incoming = core(initialValue);
        setState(prev => (sameCore(prev, incoming) ? prev : incoming));
    }, [iv.mode, iv.dateValue, iv.startDate, iv.endDate]); // eslint-disable-line react-hooks/exhaustive-deps

    const emit = (next) => {
        setState(next);
        const out = { ...next };
        if (typeof dateField === 'string' || Array.isArray(dateField)) out.dateField = dateField;
        if (includeBlank) out.includeBlank = true;
        if (onFilterChange) onFilterChange(out);
    };

    const handleModeChange = (newMode) => {
        // Day-wise opens on TODAY so it is immediately meaningful; Weekly opens on
        // the current week and Monthly on the current month. Other modes start blank.
        const now = new Date();
        let dateValue = '';
        if (newMode === 'Day-wise' || newMode === 'Weekly') dateValue = toYMD(now);
        if (newMode === 'Monthly') dateValue = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
        emit({ mode: newMode, dateValue, startDate: '', endDate: '' });
    };
    const update = (patch) => emit({ ...state, ...patch });

    const { mode, dateValue, startDate, endDate } = state;
    const offered = DATE_FILTER_MODES.filter(m => !modes || modes.includes(m.mode));
    const range = resolveDateRange(state);
    const rangeText = range && range[0] && range[1]
        ? (range[0] === range[1] ? formatDisplayDate(range[0]) : `${formatDisplayDate(range[0])} to ${formatDisplayDate(range[1])}`)
        : '';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', background: '#fff', padding: '12px 15px', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)', marginBottom: '15px', ...style }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <Calendar size={16} color="#64748b" />
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Filter By:</span>
                {offered.map(({ mode: m, label }) => (
                    <button type="button" key={m} onClick={() => handleModeChange(m)} style={btn(mode === m)}>{label}</button>
                ))}
            </div>

            {(mode === 'Today' || mode === 'Yesterday' || mode === 'This Week' || mode === 'This Month') && (
                <div style={hint} data-testid="date-filter-range">{rangeText}</div>
            )}

            {mode === 'Day-wise' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                    <DateInput value={dateValue} onChange={(e) => update({ dateValue: e.target.value })} />
                </div>
            )}

            {mode === 'Weekly' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                    <span style={hint}>Any day in the week:</span>
                    <DateInput value={dateValue} onChange={(e) => update({ dateValue: e.target.value })} />
                    {rangeText && <span style={hint}>({rangeText})</span>}
                </div>
            )}

            {mode === 'Monthly' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                    <MaskedDateField kind="month" value={dateValue} onCommit={(v) => update({ dateValue: v })} />
                </div>
            )}

            {mode === 'Range' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                    <span style={hint}>From:</span>
                    <DateInput
                        value={startDate}
                        onChange={(e) => {
                            const newStart = e.target.value;
                            if (endDate && newStart && newStart > endDate) update({ startDate: newStart, endDate: '' });
                            else update({ startDate: newStart });
                        }}
                    />
                    <span style={hint}>To:</span>
                    <DateInput value={endDate} min={startDate} onChange={(e) => update({ endDate: e.target.value })} />
                </div>
            )}
        </div>
    );
}